import { useRef, useState } from "react";
import useQueryParams from "./useQueryParams";

interface UsePaginationModel {
  page: number;
  pageSize: number;
  setPage: (pageNumber: number) => void;
  setPageSize: (pageSize: number) => void;
}

type UsePaginationProps = {
  initialPage?: number;
  initialPageSize?: number;
};

/**
 * The largest page the MIT DataGrid accepts. It throws inside its own render past this, and the
 * portal has no error boundary to catch it, so the whole screen goes blank.
 */
const maximumPageSize = 100;

/**
 * A page number or a page size read from the query string. A reader can type anything there, so a
 * value the grid would refuse is dropped for the default rather than passed on.
 * @param value
 * @param fallback
 * @param minimum
 * @param maximum
 */
function readGridNumber(value: string | undefined, fallback: number, minimum: number, maximum?: number): number {
  if (value === undefined) {
    return fallback;
  }

  const parsed = Number(value);
  if (!Number.isInteger(parsed) || parsed < minimum) {
    return fallback;
  }

  return maximum !== undefined && parsed > maximum ? fallback : parsed;
}

function usePagination(props?: UsePaginationProps): UsePaginationModel {
  const [parameters, setParameters] = useQueryParams({ paramKeys: ["page", "pageSize"] });
  const { page: urlPage, pageSize: urlPageSize } = parameters;

  const { initialPage = 0, initialPageSize = 25 } = props ?? {};
  const [page, setPageState] = useState(() => readGridNumber(urlPage, initialPage, 0));
  const [pageSize, setPageSizeState] = useState(() => readGridNumber(urlPageSize, initialPageSize, 1, maximumPageSize));

  // The query string holds both keys, and a write of one key rewrites the other. The grid reports
  // a page change and a page size change through two callbacks in one handler, where state is
  // still a render behind, so the setters read the pair from these refs instead.
  const pageRef = useRef(page);
  const pageSizeRef = useRef(pageSize);

  const setPage = (pageNumber: number) => {
    pageRef.current = pageNumber;
    setPageState(pageNumber);
    setParameters({ page: pageNumber, pageSize: pageSizeRef.current });
  };

  const setPageSize = (pageSize: number) => {
    pageSizeRef.current = pageSize;
    setPageSizeState(pageSize);
    setParameters({ page: pageRef.current, pageSize });
  };

  return {
    page,
    setPage,
    pageSize,
    setPageSize,
  };
}

export default usePagination;
